﻿import React, { createContext, ReactNode, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import { UserContext, UserContextDto } from "./UserContextProvider.types";
import { useTranslation } from "../../localization/useTranslation";
import { getJwtToken } from "../../common/api/apiShared";
import DotLegalFullScreenSpinner from "../../common/components/dotLegalFullScreenSpinner/DotLegalFullScreenSpinner";
import { LoginProvider, UserStartPage } from "../../user/User.types.ts";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useClaimStoredCustomerOnFocus } from "../customerStorage";

export const defaultPermissions = () => {
    return {
        read: false,
        create: false,
        edit: false,
        delete: false,
    };
};

export const UserContextContext = createContext<UserContext | undefined>({
    emailAddress: "",
    userProfileId: "",
    customerName: "",
    customerCreated: new Date(),
    language: "en",
    fullName: "",
    userInitials: "",
    customerId: "",
    isFirstLogin: false,
    helpLink: "",
    gdpo: false,
    germanEnabled: false,
    created: new Date(),
    businessAreas: [],
    loginProvider: LoginProvider.DotLegalIdp,
    startPage: UserStartPage.Dashboard,
    showLegacyRiskAssessments: true,
    hiddenNavigationMenuItems: [],
    permissions: {
        canManageCustomers: false,
        canManageUsers: false,
        canManageGroupEntities: false,
        canManageMasterData: false,
        canCreateMasterData: false,
        canManageRiskAssessment: false,
        canChangeUserLoginProvider: false,
        canAccessAllData: false,
        canValidateProcessingActivities: false,
        canManageProcessingActivityStandardTemplates: false,
        canManageSystemStandardTemplates: false,
        canManageStandardTemplateData: false,
        companyDocumentationPermissions: defaultPermissions(),
        processingActivityAssociationPermissions: {
            permissions: defaultPermissions(),
            hasGroupEntitiesPermission: false,
            hasBusinessAreaPermission: false,
            hasOwnershipPermission: false,
            hasAnySubPermissionAccess: false,
        },
        processingActivityDataCategoryPermissions: {
            permissions: defaultPermissions(),
        },
        processingActivityDataSubjectPermissions: {
            hasProcessorsPermission: false,
            hasDataSubjectsPermission: false,
            hasSecurityMeasurePermission: false,
            hasDataSourcePermission: false,
            hasDataSubjectCustomOnlyPermission: false,
            hasDataProcessorCustomOnlyPermission: false,
            permissions: defaultPermissions(),
        },
        processingActivityLegalBasesPermissions: {
            hasDeletionPeriodPermission: false,
            hasLegalBasisPermission: false,
            permissions: defaultPermissions(),
        },
        processingActivityMasterDataPermissions: {
            hasPurposePermission: false,
            permissions: defaultPermissions(),
        },
        processingActivitySystemPermissions: {
            permissions: defaultPermissions(),
        },
        processingActivityPolicyPermissions: {
            permissions: defaultPermissions(),
        },
        processingActivityDataControllersPermissions: {
            hasAgreementPermission: false,
            hasResponsiblePermission: false,
            hasSubProcessorPermission: false,
            hasTiaAndTransferBasisPermission: false,
            hasDataCategoriesPermission: false,
            hasNameAccess: false,
            permissions: defaultPermissions(),
        },
        processingActivityDataProcessorsPermissions: {
            hasAgreementPermission: false,
            hasResponsiblePermission: false,
            hasSubProcessorPermission: false,
            hasTiaAndTransferBasisPermission: false,
            hasDataCategoriesPermission: false,
            hasNameAccess: false,
            permissions: defaultPermissions(),
        },
        processingActivityDisclosuresPermissions: {
            hasAgreementPermission: false,
            hasBasisOfDisclosurePermission: false,
            hasResponsiblePermission: false,
            hasTransferBasisPermission: false,
            hasDataCategoriesPermission: false,
            hasNameAccess: false,
            permissions: defaultPermissions(),
        },
        processingActivitySourcesPermissions: {
            hasAgreementPermission: false,
            hasResponsiblePermission: false,
            hasDataCategoriesPermission: false,
            hasNameAccess: false,
            permissions: defaultPermissions(),
        },
        processingActivityJointDataControllerPermissions: {
            hasAgreementPermission: false,
            hasResponsiblePermission: false,
            hasDataCategoriesPermission: false,
            hasNameAccess: false,
            permissions: defaultPermissions(),
        },
        annualWheelPermission: defaultPermissions(),
        taskPermissions: defaultPermissions(),
        processingActivityPermissions: defaultPermissions(),
        systemPermissions: defaultPermissions(),
        canManageRoles: false,
        canAccessRecord301: false,
        canAccessRecord302: false,
        riskPermissions: defaultPermissions(),
        riskTemplatePermissions: defaultPermissions(),
        customListPermissions: defaultPermissions(),
        companyPermissions: defaultPermissions(),
        policyPermissions: defaultPermissions(),
        canManageProcessingActivtyComments: false,
        processingActivityStatusManagement: undefined,
        canManageProcessingActivityNote: false,
        canAccessAuditLog: false,
        canAccessProcessingActivityStatistic: false,
        canAccessTransfers: false,
        canDeleteDoocumentVersion: false,
        canSetDocumentStatus: false,
        riskVersionPermissions: defaultPermissions(),
        incidentLogPermissions: defaultPermissions(),
        processPermissions: defaultPermissions(),
        observationPermissions: defaultPermissions(),
        reportTypes: [],
        canManageAnnualWheelActivityTaskMasterData: false,
        canCopyProcessingActivity: false,
        canManageInternalAssessment: false,
        frameworkPermissions: defaultPermissions(),
        canManageAuditGroups: false,
        canAccessTags: false,
        contractManagementPermissions: {
            permissions: defaultPermissions(),
            canAccessContractsOverview: false,
            canManageGlobalViews: false,
            canManageExcludedUsers: false,
            nonEditableFields: [],
            nonEditableCustomFieldIds: [],
        },
        canAccessProcessingActivityDocuments: false,
        canCreateLegalEntitiesInline: false,
        canDoFullDataExport: false,
        globalDocumentSearchAccess: false,
        canAccessAiFeature: false,
        canManageAiAdministration: false,
        canAccessEnterpriseRiskManagement: false,
        canManageApiKeys: false,
    },
});

export interface IUserContextProviderProps {
    children: ReactNode;
}

function UserContextProvider(props: IUserContextProviderProps) {
    const { changeLanguage } = useTranslation();
    useClaimStoredCustomerOnFocus();

    const oneHour = 3600000;
    const url = "/userContext";
    const query = useDotLegalQuery<UserContextDto>(url, {
        staleTime: oneHour,
    });

    let userContext: UserContext | undefined = undefined;

    if (query.data) {
        const claims = decodeClaims(getJwtToken());

        userContext = {
            language: claims.Language,
            userProfileId: claims.UserProfileId,
            fullName: claims.FullName,
            userInitials: claims.UserInitials,
            emailAddress: claims.EmailAddress,
            customerName: claims.CustomerName,
            customerCreated: new Date(claims.CustomerCreated),
            permissions: query.data.userPermissions,
            customerId: claims.CustomerId,
            isFirstLogin: claims.LastLogOnDate === "null",
            helpLink: claims.HelpLink,
            gdpo: claims.GDPO === "True",
            germanEnabled: claims.GermanEnabled === "True",
            created: new Date(claims.Created),
            businessAreas: claims.BusinessAreas.split(";"),
            loginProvider: Number(claims.LoginProvider),
            startPage: Number(query.data.startPage),
            showLegacyRiskAssessments: query.data.showLegacyRiskAssessments,
            hiddenNavigationMenuItems: query.data.hiddenNavigationMenuItems,
        };

        if (!localStorage.getItem("language")) {
            changeLanguage(userContext.language);
        }
    }

    return (
        <UserContextContext.Provider value={userContext}>
            {query.isLoading ? <DotLegalFullScreenSpinner /> : <React.Fragment>{props.children}</React.Fragment>}
        </UserContextContext.Provider>
    );
}

export function useUserContext(): UserContext {
    return useContext(UserContextContext)!;
}

interface UserClaims {
    Language: string;
    CustomerName: string;
    CustomerCreated: Date;
    UserProfileId: string;
    EmailAddress: string;
    FullName: string;
    UserInitials: string;
    CustomerId: string;
    LastLogOnDate: string;
    HelpLink: string;
    GDPO: string;
    GermanEnabled: string;
    Created: Date;
    BusinessAreas: string;
    LoginProvider: string;
}

export function decodeClaims(token: string): UserClaims {
    let object = jwtDecode<UserClaims>(token);

    return {
        Language: object.Language,
        CustomerName: object.CustomerName,
        CustomerCreated: object.CustomerCreated,
        UserProfileId: object.UserProfileId,
        EmailAddress: object.EmailAddress,
        FullName: object.FullName,
        UserInitials: object.UserInitials,
        CustomerId: object.CustomerId,
        LastLogOnDate: object.LastLogOnDate,
        HelpLink: object.HelpLink,
        GDPO: object.GDPO,
        GermanEnabled: object.GermanEnabled,
        Created: object.Created,
        BusinessAreas: object.BusinessAreas,
        LoginProvider: object.LoginProvider,
    };
}

export default UserContextProvider;
